'use client';

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

export interface ContextMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
  badge?: string;
}

export interface ContextMenuProps {
  items: ContextMenuItem[];
  trigger: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
}

const MenuContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TriggerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuWrapper = styled.div<{ $isOpen: boolean; $align: 'left' | 'right' }>`
  position: absolute;
  top: calc(100% + 4px);
  ${({ $align }) => ($align === 'right' ? 'right: 0;' : 'left: 0;')}
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: translateY(${({ $isOpen }) => ($isOpen ? 0 : -10)}px);
  transition: all 0.2s ease;
  min-width: 200px;
  overflow: hidden;
`;

const MenuItem = styled.button<{ $variant?: 'default' | 'danger'; $disabled?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  text-align: left;
  color: ${({ $variant, $disabled }) => 
    $disabled ? '#9ca3af' : $variant === 'danger' ? '#ef4444' : '#374151'};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${({ $variant, $disabled }) => {
      if ($disabled) return 'transparent';
      return $variant === 'danger' ? 'rgba(239, 68, 68, 0.1)' : '#f5f5f5';
    }};
  }

  &:active {
    background-color: ${({ $variant, $disabled }) => {
      if ($disabled) return 'transparent';
      return $variant === 'danger' ? 'rgba(239, 68, 68, 0.15)' : '#f3f4f6';
    }};
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:only-child {
    border-radius: 8px;
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

const MenuItemContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Badge = styled.span`
  background: linear-gradient(135deg, #d33324 0%, #fb6f2a 100%);
  color: #ffffff;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MenuDivider = styled.div`
  height: 1px;
  background-color: #e5e7eb;
  margin: 4px 0;
`;

/**
 * ContextMenu Component
 * 
 * A popup menu that appears when clicking a trigger element.
 * Commonly used for action menus like edit, delete, etc.
 * 
 * @component
 * @example
 * ```tsx
 * const items = [
 *   { label: 'Edit', icon: <EditIcon />, onClick: handleEdit },
 *   { label: 'Delete', icon: <DeleteIcon />, onClick: handleDelete, variant: 'danger' }
 * ];
 * 
 * <ContextMenu 
 *   items={items}
 *   trigger={<button>...</button>}
 *   align="right"
 * />
 * ```
 */
export const ContextMenu: React.FC<ContextMenuProps> = ({
  items,
  trigger,
  align = 'right',
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleItemClick = (item: ContextMenuItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <MenuContainer ref={menuRef} className={className}>
      <TriggerButton onClick={handleToggle} type="button" aria-label="Open menu">
        {trigger}
      </TriggerButton>

      <MenuWrapper $isOpen={isOpen} $align={align}>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            <MenuItem
              onClick={() => handleItemClick(item)}
              $variant={item.variant}
              $disabled={item.disabled}
              type="button"
            >
              {item.icon && <span>{item.icon}</span>}
              <MenuItemContent>
                <span>{item.label}</span>
                {item.badge && <Badge>{item.badge}</Badge>}
              </MenuItemContent>
            </MenuItem>
            {/* Add divider before danger variant items if not first */}
            {index < items?.length - 1 && item.variant !== 'danger' && items[index + 1]?.variant === 'danger' && (
              <MenuDivider />
            )}
          </React.Fragment>
        ))}
      </MenuWrapper>
    </MenuContainer>
  );
};

export default ContextMenu;

